import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

/**
 * GA/IS 최초 서비스 이용약관 동의 전용 화면 (메인 레이아웃 밖 풀스크린).
 */
const ServiceTermsConsentScreen = () => {
  const { fetchUserProfile, updateUser, logout } = useAuth();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [contentFull, setContentFull] = useState('');
  const [versionName, setVersionName] = useState('');
  const [effectiveAt, setEffectiveAt] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 브라우저 뒤로 가기로 동의 화면을 벗어나지 못하도록 히스토리 진입 차단
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/api/service-terms/first-public');
        if (cancelled) return;
        if (!response.data) {
          setError('등록된 서비스 이용약관이 없습니다. 운영자에게 문의한 뒤 다시 로그인해 주세요.');
          return;
        }
        setContentFull(response.data.contentFull || '');
        setVersionName(response.data.versionName || '');
        setEffectiveAt(response.data.effectiveAt || null);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404) {
          setError('등록된 서비스 이용약관이 없습니다. 운영자에게 문의한 뒤 다시 로그인해 주세요.');
        } else {
          setError('약관을 불러오지 못했습니다. 네트워크를 확인 후 새로고침해 주세요.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleAgree = async () => {
    if (!checked) {
      showAlert('동의하시려면 확인란을 선택해 주세요.');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.post('/api/service-terms/accept-first');
      if (response.data) {
        updateUser(response.data);
        return;
      }
      await fetchUserProfile();
    } catch (e) {
      console.error(e);
      showAlert(e.response?.data?.error || '동의 처리에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <header className="bg-[#182370] text-white py-4 px-6 shadow">
        <h1 className="text-lg font-semibold">서비스 이용약관 동의</h1>
        <p className="text-sm text-blue-100 mt-1">
          서비스 이용을 위해 아래 약관에 동의해 주세요.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-8 max-w-3xl mx-auto w-full">
        {loading && (
          <div className="text-center text-gray-600 py-16">약관을 불러오는 중입니다...</div>
        )}
        {!loading && error && (
          <div className="bg-white rounded-lg border border-amber-200 p-6 text-gray-800">
            <p className="mb-4">{error}</p>
            <button
              type="button"
              onClick={() => { void logout(); }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              로그아웃
            </button>
          </div>
        )}
        {!loading && !error && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 text-sm text-gray-700">
              {versionName && <span className="font-medium">{versionName}</span>}
              {effectiveAt && (
                <span className="ml-2 text-gray-500">
                  시행 {new Date(effectiveAt).toLocaleString('ko-KR')}
                </span>
              )}
            </div>
            <div className="p-4 max-h-[50vh] overflow-y-auto border-b border-gray-100">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed">
                {contentFull || '(내용 없음)'}
              </pre>
            </div>
            <div className="p-4 space-y-4">
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300"
                />
                <span>위 서비스 이용약관의 내용을 확인하였으며 이에 동의합니다.</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={handleAgree}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-[#182370] rounded-md hover:bg-blue-900 disabled:opacity-50"
                >
                  {submitting ? '처리 중...' : '동의하고 계속하기'}
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => { void logout(); }}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  로그아웃
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceTermsConsentScreen;
